/**
 * The drawer's "how to fix" section: the shared caption ("How to fix this
 * issue" when a fix guide is present, else "Help and resources") over a compact
 * row of teal links. Each resource is one quiet link - branded glyph + short
 * type label ("Fix Guide", "Video", ...) - rather than the old long names
 * + per-link descriptions. The full resource name rides each link's title.
 *
 * Link styling mirrors SourceLinksRow so the drawer's two link rows match; the
 * caption reuses SectionHeading so it stays identical to the Why caption.
 */
import React from 'react'
import { Box, Link, Stack } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import type { ReferenceLink } from '../../../shared/referenceLinks/types'
import {
  LINK_STYLES,
  FALLBACK_LINK_STYLE,
  referenceLinkTypeLabel,
} from '../../../shared/referenceLinks/linkStyles'
import { orderReferenceLinks } from './howToFix'
import SectionHeading from './SectionHeading'
import { poolwerxColors } from '../../../../theme/colors'

// Site-relative or http(s) only - blocks javascript:/data:/etc. before the URL
// reaches an href (matches ExceptionDetailsSection's guard).
const isSafeUrl = (url: string): boolean => {
  const u = url.trim()
  return u.startsWith('/') || /^https?:\/\//i.test(u)
}

const styleFor = (linkType: string) =>
  (LINK_STYLES as Record<string, typeof FALLBACK_LINK_STYLE>)[linkType] ??
  FALLBACK_LINK_STYLE

interface FixResourceLinksProps {
  links?: ReferenceLink[] | null
}

const FixResourceLinks: React.FC<FixResourceLinksProps> = ({ links }) => {
  const ordered = orderReferenceLinks(links).filter(
    (l) => l.url && isSafeUrl(l.url),
  )
  if (ordered.length === 0) return null

  // Caption matches the legacy heading: a fix guide makes it actionable
  // ("How to fix this issue"); otherwise it's supporting material.
  const heading = ordered.some((l) => l.link_type === 'fix_guide')
    ? 'How to fix this issue'
    : 'Help and resources'

  return (
    <Box>
      <SectionHeading>{heading}</SectionHeading>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2.5}>
        {ordered.map((link) => {
          const { icon: Glyph, fg } = styleFor(link.link_type)
          const label = referenceLinkTypeLabel(link.link_type)
          // fix_guide's branded glyph is already the external-link arrow, so the
          // leading glyph doubles as the "opens in a new tab" cue. Other types
          // (book/play/info/agent) get a trailing arrow to signal the same.
          const isExternalGlyph = Glyph === OpenInNewIcon
          return (
            <Link
              key={`${link.link_type}-${link.url}`}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={link.name || label}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '12.5px',
                fontWeight: 500,
                color: poolwerxColors.teal,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <Glyph sx={{ fontSize: 14, color: fg, flexShrink: 0 }} />
              {label}
              {!isExternalGlyph && <OpenInNewIcon sx={{ fontSize: 12 }} />}
            </Link>
          )
        })}
      </Stack>
    </Box>
  )
}

export default FixResourceLinks
